/**
 * MitzvotCard - Card individual de mandamiento
 * Vista resumida con link a detalle
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronRight, Check, X } from 'lucide-react';

const MitzvotCard = ({ mitzvah, viewMode = 'grid' }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/mandamientos/${mitzvah.id}`);
  };

  // Determinar tipo de mandamiento
  const isPositive = mitzvah.asehOrLoSaseh === 'A';
  const typeColor = isPositive ? 'text-green-600' : 'text-red-600';
  const typeBg = isPositive ? 'bg-green-50' : 'bg-red-50';
  const typeIcon = isPositive ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />;

  // Contar perspectivas disponibles
  const perspectivesCount = [
    mitzvah.rambamPerspectives?.length,
    mitzvah.rambanPerspectives?.length,
    mitzvah.chinuchPerspectives?.length,
    mitzvah.semagPerspectives?.length,
    mitzvah.bahagPerspectives?.length,
    mitzvah.hebrewRootsPerspectives?.length
  ].filter(count => count && count > 0).length;

  if (viewMode === 'list') {
    // Vista lista (horizontal)
    return (
      <div
        onClick={handleClick}
        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer flex items-center gap-4"
      >
        {/* Número */}
        <div className="flex-shrink-0">
          <div 
            className="w-16 h-16 rounded-full bg-gray-500 flex items-center justify-center"
          >
            <span className="text-white text-xl font-bold">
              {mitzvah.mitzvahNumber}
            </span>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${typeBg} ${typeColor}`}>
              {typeIcon}
              {isPositive ? 'Hacer' : 'No Hacer'}
            </span>
          </div>
          
          <h3 className="font-semibold text-gray-900 mb-1 truncate">
            {mitzvah.mitzvahNameEs || mitzvah.mitzvahNameEn || mitzvah.mitzvahName}
          </h3>
          
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              {perspectivesCount} perspectivas
            </span>
          </div>
        </div>

        {/* Flecha */}
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </div>
    );
  }

  // Vista grid (card vertical)
  return (
    <div
      onClick={handleClick}
      className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-full bg-gray-500 flex items-center justify-center">
          <span className="text-white text-lg font-bold">
            {mitzvah.mitzvahNumber}
          </span>
        </div>
        
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${typeBg} ${typeColor}`}>
          {typeIcon}
          {isPositive ? 'Hacer' : 'No Hacer'}
        </span>
      </div>

      {/* Contenido */}
      <div className="mb-4">
        <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2">
          {mitzvah.mitzvahNameEs || mitzvah.mitzvahNameEn || mitzvah.mitzvahName}
        </h3>
        
        {mitzvah.mitzvahName && (
          <p className="text-sm text-gray-600 font-hebrew" dir="rtl">
            {mitzvah.mitzvahName}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1 text-gray-600">
          <BookOpen className="w-4 h-4" />
          {perspectivesCount} perspectivas
        </span>
        
        <span className="text-blue-600 flex items-center gap-1">
          Ver detalle
          <ChevronRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
};

export default MitzvotCard;

